import { useTrackComponentEvent } from 'components/forms/TimePicker/hooks/useTrackComponentEvent';
import { ComponentTrackingParams } from 'components/forms/TimePicker/types';

// Keep event factories (pure functions)
export const messageOwnerEvents = {
  messageOwnerButton: (objectProperties: {
    boat_public_id: string;
    isAuthenticated: boolean;
  }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object: 'message_owner',
    context: 'your_crew',
    object_properties: objectProperties,
  }),

  seeProfileLink: (objectProperties: { boat_public_id: string }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'text_link',
    object: 'see_profile',
    context: 'your_crew',
    object_properties: objectProperties,
  }),

  messageOwnerWidgetShow: (objectProperties: {
    date: string;
    duration: string;
    passengers: number;
    captained: boolean;
    booking_total: number;
  }): ComponentTrackingParams => ({
    event_name: 'show',
    component_type: 'draw',
    object: 'message_owner_widget',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  adjustPassengersButton: (
    action: 'minus' | 'plus',
    objectProperties: { passengers: number }
  ): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object: `${action}_passengers`,
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickTextBox: (): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'text_box',
    object: 'message_to_owner',
    context: 'message_owner_widget',
  }),

  clickDatePicker: (objectProperties: { trip_date: string }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'pop_up_with_buttons',
    object: 'trip_date_picker',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickDurationPicker: (objectProperties: { duration: string }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'pop_up_with_buttons',
    object: 'duration_picker',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickStartTimePicker: (objectProperties: { start_time: string }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'pop_up_with_buttons',
    object: 'start_time_picker',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickCaptainButton: (object: 'captain_button' | 'no_captain_button'): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object,
    context: 'message_owner_widget',
  }),

  clickSubmitSendMessageButton: (objectProperties: {
    booking_id: string;
    booking_total: number;
    filled_values: {
      trip_date: string;
      duration: string;
      passengers: number;
      captained: boolean;
    };
  }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object: 'send_message',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  showConfirmation: (objectProperties: {
    boat_public_id: string;
    text: string;
    prompt: string;
  }): ComponentTrackingParams => ({
    event_name: 'show',
    component_type: 'draw',
    object: 'message_sent_confirmation',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickViewConversationButton: (objectProperties: {
    boat_public_id: string;
    conversation_id: string;
    text: 'View Conversation';
    boat_recommendations_shown: boolean;
  }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object: 'view_conversation',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  clickKeepExploringButton: (objectProperties: {
    boat_public_id: string;
    text: 'Keep Exploring';
  }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'button',
    object: 'keep_exploring',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),

  showRecommendedBoats: (objectProperties: {
    boat_public_id: string;
    boats_display?: Array<{
      boat_public_id: string;
      position: number;
    }>;
  }): ComponentTrackingParams => ({
    event_name: 'show',
    component_type: 'horizontal_display_boat_cards',
    object: 'recommended_for_you',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),
  scrollRecommendedBoats: (objectProperties: {
    boat_public_id: string;
    scroll_direction: 'left' | 'right';
    boats_display?: Array<{
      boat_public_id: string;
      position: number;
    }>;
  }): ComponentTrackingParams => ({
    event_name: 'scroll',
    component_type: 'scroll_horizontal_display_boat_cards',
    object: 'recommended_for_you',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),
  clickRecommendedBoatCard: (objectProperties: {
    boat_public_id: string;
    position: number;
  }): ComponentTrackingParams => ({
    event_name: 'click',
    component_type: 'boat_card',
    object: 'boat_card',
    context: 'message_owner_widget',
    object_properties: objectProperties,
  }),
} as const;

export const useMessageOwnerTracking = () => {
  const trackComponentEvent = useTrackComponentEvent();

  const createTracker =
    <T extends (...args: any[]) => ComponentTrackingParams>(eventFactory: T) =>
    (...args: Parameters<T>) =>
      trackComponentEvent(eventFactory(...args));

  return {
    trackMessageOwnerButton: createTracker(messageOwnerEvents.messageOwnerButton),
    trackSeeProfileLink: createTracker(messageOwnerEvents.seeProfileLink),
    trackMessageOwnerWidgetShow: createTracker(messageOwnerEvents.messageOwnerWidgetShow),
    trackPassengerAdjustment: createTracker(messageOwnerEvents.adjustPassengersButton),
    trackClickTextBox: createTracker(messageOwnerEvents.clickTextBox),
    trackClickDatePicker: createTracker(messageOwnerEvents.clickDatePicker),
    trackClickDurationPicker: createTracker(messageOwnerEvents.clickDurationPicker),
    trackClickStartTimePicker: createTracker(messageOwnerEvents.clickStartTimePicker),
    trackClickCaptainButton: createTracker(messageOwnerEvents.clickCaptainButton),
    trackClickSubmitSendMessageButton: createTracker(messageOwnerEvents.clickSubmitSendMessageButton),
    trackShowConfirmation: createTracker(messageOwnerEvents.showConfirmation),
    trackClickViewConversationButton: createTracker(messageOwnerEvents.clickViewConversationButton),
    trackClickKeepExploringButton: createTracker(messageOwnerEvents.clickKeepExploringButton),
    trackShowRecommendedBoats: createTracker(messageOwnerEvents.showRecommendedBoats),
    trackScrollRecommendedBoats: createTracker(messageOwnerEvents.scrollRecommendedBoats),
    trackClickRecommendedBoatCard: createTracker(messageOwnerEvents.clickRecommendedBoatCard),

    // Generic method for custom tracking
    track: (eventFactory: ComponentTrackingParams) => {
      trackComponentEvent(eventFactory);
    },
  };
};
